import classNames from 'classnames'
import React, { type ReactNode, useState } from 'react'
import type { Campaign } from '../../types/campaign'
import { formatPlacementAdjustment } from '../../util/placement_adjustments'
import { Popover } from '../ui/popover'
import { CampaignBidAdjustmentsModal } from './CampaignBidAdjustmentsModal'

export type CampaignBidAdjustmentValues = Pick<
    Campaign,
    'bid_adjustment_top_of_search' | 'bid_adjustment_rest_of_search' | 'bid_adjustment_product_page'
>

export const CAMPAIGN_BID_ADJUSTMENTS = [
    { key: 'bid_adjustment_top_of_search', label: 'Top of Search' },
    { key: 'bid_adjustment_rest_of_search', label: 'Rest of Search' },
    { key: 'bid_adjustment_product_page', label: 'Product Pages' },
] as const satisfies ReadonlyArray<{
    key: keyof CampaignBidAdjustmentValues
    label: string
}>

export const campaignBidAdjustmentCount = (campaign: CampaignBidAdjustmentValues): number =>
    CAMPAIGN_BID_ADJUSTMENTS.filter(({ key }) => (campaign[key] ?? 0) > 0).length

const bidAdjustmentsAriaLabel = (count: number): string =>
    `${count} non-default bid adjustment${count === 1 ? '' : 's'}`

const adjustmentRows = (campaign: CampaignBidAdjustmentValues) =>
    CAMPAIGN_BID_ADJUSTMENTS.map(({ key, label }) => ({
        key,
        label,
        // Null/absent and 0 are the same default state in Amazon's contract.
        value: campaign[key] ?? 0,
    }))

const BidAdjustmentsPopover = ({
    campaign,
    children,
}: {
    campaign: CampaignBidAdjustmentValues
    children: ReactNode
}) => {
    const content = (
        <div className="p-2">
            {adjustmentRows(campaign).map(({ key, label, value }) => (
                <div key={key} className="flex justify-between gap-4 py-1">
                    <span>{label}</span>
                    <span>{formatPlacementAdjustment(value)}</span>
                </div>
            ))}
        </div>
    )

    return (
        <Popover content={content} placement="bottom" interactionKind="hover">
            {children}
        </Popover>
    )
}

export const CampaignBidAdjustmentsCell = ({
    data,
    onEdit,
}: {
    data: CampaignBidAdjustmentValues
    /** Absent leaves the cell read-only, as it was before MER-2987. */
    onEdit?: () => void
}) => {
    const count = campaignBidAdjustmentCount(data)

    return (
        <BidAdjustmentsPopover campaign={data}>
            <button
                type="button"
                className={classNames(
                    'border-0 border-b border-dashed border-gray-400 bg-transparent p-0 text-inherit',
                    onEdit ? 'cursor-pointer' : 'cursor-help',
                )}
                aria-label={
                    onEdit
                        ? `Edit bid adjustments (${bidAdjustmentsAriaLabel(count)})`
                        : bidAdjustmentsAriaLabel(count)
                }
                onClick={onEdit}
            >
                {count}
            </button>
        </BidAdjustmentsPopover>
    )
}

export interface CampaignBidAdjustmentsEditorProps {
    campaign: CampaignBidAdjustmentValues & {
        campaign_id: unknown
        campaign_name?: string
    }
}

/**
 * The cell plus its editor. The count and the hover popover stay exactly as they
 * were; clicking now opens the edit dialog (MER-2987).
 */
export const CampaignBidAdjustmentsEditor = ({ campaign }: CampaignBidAdjustmentsEditorProps) => {
    const [isOpen, setIsOpen] = useState(false)

    return (
        <>
            <CampaignBidAdjustmentsCell data={campaign} onEdit={() => setIsOpen(true)} />
            {isOpen && (
                <CampaignBidAdjustmentsModal
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                    campaign={campaign}
                />
            )}
        </>
    )
}
